import Phaser from 'phaser';
import type { BossGust } from '@/net/api';
import halloween from '../../../server/api/src/game-config/halloween.config.json';
import { drawHalloweenGustStream } from './halloweenGustStream';

const OPEN = 'boss_halloween_candy_glutton';
const HALF = `${OPEN}_blink_half`;
const CLOSED = `${OPEN}_blink_closed`;
const DAZED = `${OPEN}_dazed`;
const INHALE = `${OPEN}_inhale`;
const BLOW = `${OPEN}_blow`;
const COLORS = [0xffdf8e, 0xffefca, 0xcfa7ed];
const REST_SCALE = 0.7;
const REST_Y = 30;

/** Cosmetic only: bounded emitters and one composed pose, independent of rewards. */
export class HalloweenBossPresentation {
  readonly sprite: Phaser.GameObjects.Sprite;
  private readonly shadow: Phaser.GameObjects.Ellipse;
  private readonly halo: Phaser.GameObjects.Ellipse;
  private readonly sparks: Phaser.GameObjects.Particles.ParticleEmitter;
  private readonly confetti: Phaser.GameObjects.Particles.ParticleEmitter;
  private readonly landingRing: Phaser.GameObjects.Ellipse;
  private readonly accent: Phaser.GameObjects.Container;
  private readonly accentText: Phaser.GameObjects.Text;
  private readonly victoryStars: Phaser.GameObjects.Container;
  private readonly idle = { phase: 0 };
  private readonly hitPose = { amount: 0 };
  private readonly hopPose = { x: 0, lift: 0, squash: 0, angle: 0 };
  private readonly victory = { scale: 1, x: 0, y: 0, angle: 0 };
  private readonly idleTween: Phaser.Tweens.Tween;
  private hitTween?: Phaser.Tweens.Tween;
  private danceTween?: Phaser.Tweens.TweenChain;
  private victoryTween?: Phaser.Tweens.TweenChain;
  private accentTween?: Phaser.Tweens.TweenChain;
  private ringTween?: Phaser.Tweens.Tween;
  private baseY = 300;
  private size = 380;
  private playing = false;
  private won = false;
  private lastHitAt = -Infinity;
  private lastBurstAt = -Infinity;
  private nextDanceAt = 0;
  private danceCount = 0;
  private celebrating = false;
  private dazed = false;
  private readonly wind: Phaser.GameObjects.Graphics;
  private gust?: BossGust;
  private gustNow = 0;
  private gustStage = '';

  constructor(private readonly scene: Phaser.Scene) {
    this.halo = scene.add.ellipse(450, 425, 410, 105, 0xffcd7f, 0.16)
      .setStrokeStyle(3, 0xd5b0eb, 0.35).setDepth(-6);
    this.shadow = scene.add.ellipse(450, 425, 285, 65, 0x76604c, 0.14).setDepth(-5);
    this.sprite = scene.add.sprite(450, 300, OPEN).setDepth(3).setName('halloween-boss');
    this.wind = scene.add.graphics().setDepth(10).setName('halloween-boss-wind');
    // Local animation dies with this sprite; no global frame references survive unloading.
    this.sprite.anims.create({
      key: 'blink', frameRate: 25, repeat: -1,
      frames: [
        { key: OPEN, duration: 2900 }, { key: HALF, duration: 10 },
        { key: CLOSED, duration: 60 }, { key: HALF, duration: 10 }, { key: OPEN },
      ],
    });
    this.sprite.play('blink');
    this.idleTween = scene.tweens.add({ targets: this.idle, phase: Math.PI * 2,
      duration: 3400, repeat: -1, ease: 'Linear' });
    this.sparks = scene.add.particles(0, 0, 'fx-sparkle-star', {
      emitting: false, maxAliveParticles: 64, lifespan: { min: 300, max: 580 },
      speed: { min: 35, max: 115 }, angle: { min: 195, max: 345 }, gravityY: 85,
      scale: { start: 0.65, end: 0 }, alpha: { start: 0.85, end: 0 },
      rotate: { min: 0, max: 180 }, tint: COLORS,
    }).setDepth(12).setName('halloween-boss-sparks');
    const candyWidth = scene.textures.get('ui_candy').getSourceImage().width;
    this.confetti = scene.add.particles(0, 0, 'ui_candy', {
      emitting: false, maxAliveParticles: 24, lifespan: { min: 850, max: 1400 },
      speed: { min: 110, max: 270 }, angle: { min: 205, max: 335 }, gravityY: 330,
      scale: { start: 26 / candyWidth, end: 10 / candyWidth },
      alpha: { start: 0.85, end: 0 }, rotate: { min: -180, max: 180 }, tint: COLORS,
    }).setDepth(14);
    this.landingRing = scene.add.ellipse(450, 425, 145, 38)
      .setStrokeStyle(4, 0xffe2a0, 0.85).setDepth(-4).setVisible(false);
    const bubble = scene.add.graphics();
    bubble.fillStyle(0xfff6df, 0.97).fillRoundedRect(-92, -33, 184, 66, 22)
      .fillTriangle(-65, 25, -88, 49, -34, 28);
    bubble.lineStyle(3, 0xa783bf).strokeRoundedRect(-92, -33, 184, 66, 22);
    this.accentText = scene.add.text(0, 0, '', { fontFamily: 'Nunito, sans-serif',
      fontSize: '30px', fontStyle: 'bold', color: '#79508c' }).setOrigin(0.5);
    this.accent = scene.add.container(450, 200, [bubble, this.accentText])
      .setDepth(20).setVisible(false).setName('halloween-boss-comic');
    const stars = [0, 1, 2].map(i => scene.add.star(0, 0,
      5, 9, 19, i === 1 ? 0xffd46c : 0xffefb2).setStrokeStyle(2, 0xc58b4f));
    this.victoryStars = scene.add.container(450, 200, stars).setDepth(8).setVisible(false);
    this.reset(300, 380);
  }

  reset(baseY: number, size: number, won = false): void {
    this.hitTween?.remove(); this.victoryTween?.stop();
    this.danceTween?.stop(); this.accentTween?.stop(); this.ringTween?.remove();
    this.celebrating = false;
    Object.assign(this.hopPose, { x: 0, lift: 0, squash: 0, angle: 0 });
    this.accent.setVisible(false); this.landingRing.setVisible(false);
    this.dazed = false;
    this.victoryStars.setVisible(won);
    this.nextDanceAt = this.scene.time.now + 1400;
    this.hitPose.amount = 0;
    this.baseY = baseY; this.size = size; this.won = won;
    this.victory.scale = won ? REST_SCALE : 1;
    this.victory.x = 0;
    this.victory.y = won ? REST_Y : 0;
    this.victory.angle = won ? 10 : 0;
    if (won) this.becomeDazed();
    else this.sprite.play('blink', true);
    this.update();
  }

  setPlaying(playing: boolean): void { this.playing = playing; }

  setGust(gust: BossGust | undefined, now: number): void {
    const active = !this.won && gust && now >= gust.startedAt && now < gust.endsAt;
    const stage = active ? now < gust.blowAt ? 'inhale' : 'blow' : '';
    if (stage !== this.gustStage || (stage && gust?.index !== this.gust?.index)) {
      this.danceTween?.stop(); this.hitTween?.remove(); this.hitPose.amount = 0;
      Object.assign(this.hopPose, { x: 0, lift: 0, squash: 0, angle: 0 });
      this.nextDanceAt = this.scene.time.now + 3200;
      if (stage) {
        this.showAccent(stage === 'inhale' ? 'Huuu...' : 'FUUUU!', stage === 'inhale' ? 1500 : 2300);
      } else if (!this.won) this.sprite.play('blink', true);
    }
    if (stage) {
      this.sprite.anims.stop(); this.sprite.setTexture(stage === 'inhale' ? INHALE : BLOW);
    }
    this.gustStage = stage; this.gust = gust; this.gustNow = now;
  }

  hit(): void {
    if (!this.playing || this.won || this.gustStage || this.scene.time.now - this.lastHitAt < 700) return;
    this.lastHitAt = this.scene.time.now;
    this.hitTween?.remove();
    this.hitTween = this.scene.tweens.add({ targets: this.hitPose,
      amount: { from: 1, to: 0 }, duration: 420, ease: 'Cubic.easeOut' });
    this.sparks.explode(5, this.sprite.x + Phaser.Math.Between(-65, 65), this.baseY + 100);
  }

  candyBurst(): void {
    if (this.scene.time.now - this.lastBurstAt < 350) return;
    this.lastBurstAt = this.scene.time.now;
    this.sparks.explode(4, this.sprite.x, this.sprite.y + 55);
  }

  pickup(x: number, y: number, value: number): void {
    this.sparks.explode(value >= 5 ? 5 : 3, x, y);
  }

  private showAccent(text: string, holdMs = 540): void {
    this.accentTween?.stop();
    this.accentText.setText(text);
    this.accent.setVisible(true).setAlpha(0).setScale(0.7);
    this.accentTween = this.scene.tweens.chain({ targets: this.accent, tweens: [
      { alpha: 1, scale: 1, duration: 170, ease: 'Back.easeOut' },
      { alpha: 0, scale: 1.08, delay: holdMs, duration: 230,
        onComplete: () => this.accent.setVisible(false) },
    ] });
  }

  private land(): void {
    this.sparks.explode(4, this.sprite.x, this.baseY + this.victory.y + this.size * this.victory.scale * 0.34);
    this.ringTween?.remove();
    this.landingRing.setPosition(this.sprite.x, this.baseY + this.victory.y + this.size * this.victory.scale * 0.34)
      .setScale(1).setAlpha(0.7).setVisible(true);
    this.ringTween = this.scene.tweens.add({ targets: this.landingRing, scaleX: 1.55,
      scaleY: 1.4, alpha: 0, duration: 380, onComplete: () => this.landingRing.setVisible(false) });
  }

  private dance(): void {
    const side = this.danceCount++ % 2 === 0 ? -1 : 1;
    this.showAccent(side === -1 ? 'Boing!' : 'Hop-siup!');
    this.nextDanceAt = this.scene.time.now + 3200;
    this.danceTween = this.scene.tweens.chain({ targets: this.hopPose, tweens: [
      { squash: 0.13, duration: 85, ease: 'Sine.easeIn' },
      { x: side * 68, lift: 62, squash: -0.09, angle: side * 13, duration: 210, ease: 'Sine.easeOut' },
      { lift: 0, squash: 0.15, angle: side * 5, duration: 125, ease: 'Quad.easeIn', onComplete: () => this.land() },
      { x: -side * 68, lift: 54, squash: -0.08, angle: -side * 12, duration: 270, ease: 'Sine.easeOut' },
      { lift: 0, squash: 0.13, angle: -side * 4, duration: 125, ease: 'Quad.easeIn', onComplete: () => this.land() },
      { x: 0, squash: 0, angle: 0, duration: 200, ease: 'Back.easeOut' },
    ] });
  }

  celebrate(): void {
    if (this.won) return;
    this.won = true;
    this.celebrating = true;
    this.hitTween?.remove(); this.hitPose.amount = 0;
    this.danceTween?.stop();
    Object.assign(this.hopPose, { x: 0, lift: 0, squash: 0, angle: 0 });
    this.victoryTween?.stop();
    this.showAccent('Hura!', 1200);
    this.victoryTween = this.scene.tweens.chain({ targets: this.victory, tweens: [
      { scale: 1.08, x: -38, y: -28, angle: -14, duration: 320, ease: 'Sine.easeOut' },
      { x: 50, y: -60, angle: 16, duration: 450, ease: 'Sine.easeInOut' },
      { x: -50, y: -15, angle: -18, duration: 400, ease: 'Sine.easeInOut' },
      { x: 0, y: -100, angle: 360, scale: 0.9, duration: 1000, ease: 'Cubic.easeInOut',
        onComplete: () => {
          this.confetti.explode(24, this.sprite.x, this.sprite.y + 35);
          this.sparks.explode(18, this.sprite.x, this.sprite.y + 35);
          this.becomeDazed();
          this.showAccent('Ojej!', 1400);
        } },
      { x: -18, y: 4, angle: 352, duration: 420, ease: 'Bounce.easeOut' },
      { x: 18, y: 0, angle: 368, duration: 420, ease: 'Sine.easeInOut' },
      { x: 0, y: REST_Y, scale: REST_SCALE, angle: 370, delay: 1200, duration: 1100, ease: 'Sine.easeInOut',
        onComplete: () => {
          this.celebrating = false; this.victory.angle = 10;
          this.confetti.explode(12, this.sprite.x, this.sprite.y + 35);
          this.land();
        } },
    ] });
  }

  private becomeDazed(): void {
    this.dazed = true;
    this.sprite.anims.stop();
    this.sprite.setTexture(DAZED);
  }

  update(): void {
    if (this.playing && !this.won && !this.gustStage && this.scene.time.now >= this.nextDanceAt) this.dance();
    const wave = Math.sin(this.idle.phase);
    const hit = this.hitPose.amount;
    const breath = this.gustStage ? 0 : this.won ? (this.celebrating ? 0 : 0.008) : 0.018;
    const size = this.size * this.victory.scale;
    const hop = this.hopPose;
    const inhale = this.gustStage === 'inhale' && this.gust
      ? Phaser.Math.Clamp((this.gustNow - this.gust.startedAt) / (this.gust.blowAt - this.gust.startedAt), 0, 1) : 0;
    const puff = this.gustStage === 'blow' && this.gust
      ? Math.sin(Phaser.Math.Clamp((this.gustNow - this.gust.blowAt) / (this.gust.endsAt - this.gust.blowAt), 0, 1) * Math.PI) : 0;
    // Compose all transforms once, so hits cannot overwrite breathing or the win pose.
    this.sprite.setDisplaySize(size * (1 + wave * breath + hit * 0.07 + hop.squash + inhale * 0.10 + puff * 0.15),
      size * (1 - wave * breath * 0.65 - hit * 0.06 - hop.squash * 0.75 + inhale * 0.12 - puff * 0.12));
    this.sprite.setPosition(450 + hop.x + this.victory.x,
      this.baseY + this.victory.y - wave * (this.won ? 2 : 6) - hit * 11 - hop.lift);
    this.sprite.setAngle(this.victory.angle + hop.angle + wave * 0.7 + Math.sin(hit * Math.PI * 2) * hit * 2);
    const floorY = this.baseY + Math.max(0, this.victory.y) + size * 0.34;
    this.shadow.setPosition(this.sprite.x, floorY).setDisplaySize(size * 0.75, size * 0.17)
      .setScale(this.shadow.scaleX * (1 - hit * 0.08 - hop.lift / 300), this.shadow.scaleY);
    this.halo.setPosition(450, floorY).setDisplaySize(size * (1.08 + wave * 0.025), size * 0.28)
      .setAlpha(this.playing ? 0.7 + wave * 0.15 : 0.25);
    this.accent.setPosition(Math.min(790, this.sprite.x + size * 0.5 + 105), this.sprite.y - size * 0.2);
    this.drawWind(size);
    this.victoryStars.setVisible(this.won && this.dazed)
      .setPosition(this.sprite.x, this.sprite.y - size * 0.46 - 16);
    // Separate elliptical orbits, with smaller, dimmer stars on the far side.
    this.victoryStars.list.forEach((child, i) => {
      const star = child as Phaser.GameObjects.Star;
      const phase = this.idle.phase * 2 + i * Math.PI * 2 / 3;
      const near = (Math.sin(phase) + 1) / 2;
      star.setPosition(Math.cos(phase) * (size * 0.32 + 16), Math.sin(phase) * 18)
        .setScale(0.75 + near * 0.3).setAlpha(0.65 + near * 0.35).setRotation(-phase);
    });
  }

  private drawWind(size: number): void {
    this.wind.clear();
    if (!this.gustStage || !this.gust) return;
    const x = this.sprite.x, y = this.sprite.y + size * 0.12;
    if (this.gustStage === 'inhale') {
      for (let i = 0; i < 7; i++) {
        const p = ((this.gustNow - this.gust.startedAt + i * 135) % 950) / 950;
        const angle = i * Math.PI * 2 / 7;
        const radius = 240 * (1 - p) + 50;
        this.wind.lineStyle(3, 0xfff5db, Math.sin(p * Math.PI) * 0.65);
        this.wind.lineBetween(x + Math.cos(angle) * radius, y + Math.sin(angle) * radius * 0.55,
          x + Math.cos(angle) * (radius + 35), y + Math.sin(angle) * (radius + 35) * 0.55);
      }
      return;
    }
    const age=this.gustNow-this.gust.blowAt, duration=halloween.boss.gust.windMs;
    const fade=Phaser.Math.Clamp((duration-age)/550,0,1);
    if (!fade) return;
    drawHalloweenGustStream(this.wind,x,y,age,fade);
    for(let ring=0;ring<6;ring++) {
      const p=(age-ring*370)/1600;
      if(p<0||p>1)continue;
      this.wind.fillStyle(0xecfff2,(1-p)*0.10*fade);
      this.wind.fillEllipse(x,y+p*320,140+p*2300,80+p*1700);
      this.wind.lineStyle(15*(1-p)+4,0xfff9e6,(1-p)*0.85*fade);
      this.wind.strokeEllipse(x,y+p*320,140+p*2300,80+p*1700);
      this.wind.lineStyle(5,0x91d2c3,(1-p)*0.65*fade);
      this.wind.strokeEllipse(x,y+p*320+18,110+p*2300,60+p*1700);
    }
    for (let i = 0; i < 24; i++) {
      const p=((age+i*93)%1050)/1050;
      const angle = i * Math.PI * 2 / 24;
      const radius = 80 + p * 1250;
      this.wind.lineStyle(5+(i%3),i%2?0xfff9df:0xb5e6db,(1-p)*0.85*fade);
      this.wind.beginPath();
      for (let j = 0; j <= 8; j++) {
        const r = radius - j * 18, curl = angle + Math.sin(j / 8 * Math.PI) * 0.12;
        const px = x + Math.cos(curl) * r, py = y + Math.sin(curl) * r * 0.8 + p * 320;
        if (!j) this.wind.moveTo(px, py); else this.wind.lineTo(px, py);
      }
      this.wind.strokePath();
    }
  }

  destroy(): void {
    this.idleTween.remove(); this.hitTween?.remove(); this.victoryTween?.stop();
    // TweenChain.remove(child) removes a child; stop() cancels the entire sequence.
    this.danceTween?.stop(); this.accentTween?.stop(); this.ringTween?.remove();
    this.sparks.destroy(); this.confetti.destroy();
    this.wind.destroy();
  }
}
